/**
 * Auth API module — bootstrap and extension handoff.
 */
import type { AuthBootstrapResponse } from '@overlayqa/shared';
import { fetchApi } from './client';
import { log } from '@/lib/logger';
import { MUTATION_TIMEOUT_MS } from './requestTimeout';

log.info('[api/auth] Module loaded');

/**
 * Upper bound on the one request that gates the ENTIRE signed-in app.
 *
 * `useAppSession` -> `authApi.bootstrap` is what `Layout` waits on, and while it
 * is in flight the app renders `AnimatedLogoLoader` and nothing else. Before
 * fetchApi accepted a bound, a request that stalled rather than failed never
 * settled, so `isLoading` never went false and the user sat on an animated logo
 * forever with no message and nothing to click (GitHub issue #75).
 *
 * 20s: the route does a JIT user upsert plus indexed reads and answers in
 * well under a second when healthy, and content lands 1.0-3.1s after `load`
 * even on a cold shell. 20s clears a Railway cold start with room to spare
 * while still firing inside the window where a real user gives up (the
 * 2026-08-12 incident user abandoned at ~19.2s). It matches
 * AUTH_CALLBACK_FAILURE_DEADLINE_MS, which was raised to 20s for the same
 * slow-connection reason.
 *
 * Bounding it means the failure lands on Layout's LoadError with a Try again,
 * and `shouldRetryApiQuery` deliberately does not retry it (see retryPolicy.ts),
 * so 20s is the real ceiling rather than 40s plus backoff.
 */
export const BOOTSTRAP_TIMEOUT_MS = 20_000;

export const authApi = {
  bootstrap: (
    token: string | undefined,
    // signupSource: attribution label carried onto the server-side
    // account_created event (e.g. 'free-tool-annotate' for a free-scan-to-account
    // conversion). Optional; omitted values are dropped by JSON.stringify so the
    // server falls back to 'direct'. See useAppSession + lib/freeScanClaim (OVE-4).
    data: { email: string; name: string; avatarUrl?: string | null; signupSource?: string; timeZone?: string }
  ) =>
    fetchApi<AuthBootstrapResponse>('/auth/bootstrap', {
      token,
      method: 'POST',
      body: JSON.stringify(data),
      timeoutMs: BOOTSTRAP_TIMEOUT_MS,
    }),

  completeExtensionHandoff: (
    token: string | undefined,
    data: { code: string; teamId: string }
  ) =>
    fetchApi<{ completed: true }>('/auth/extension/complete', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    }),

  /** Get an extension session token directly (for dashboard auto-auth). */
  getExtensionSessionToken: (
    token: string | undefined,
    data: { teamId: string }
  ) =>
    fetchApi<{
      sessionToken: string;
      userId: string;
      teamId: string;
      plan: string | null;
      email: string | undefined;
      onboardingCompleted: boolean;
    }>('/auth/extension/session-token', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    }),
};
